import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "leet-reply-audio-playing"

export default class extends Controller {
  static targets = ["audio", "button", "label"]
  static values = {
    defaultOn: { type: Boolean, default: false },
    volume: { type: Number, default: 0.28 }
  }

  connect() {
    this.sync = this.sync.bind(this)
    this.unlockPlayback = this.unlockPlayback.bind(this)
    this.audioTarget.volume = this.volumeValue
    this.audioTarget.addEventListener("play", this.sync)
    this.audioTarget.addEventListener("pause", this.sync)

    if (this.shouldPlayByDefault()) {
      this.play()
    } else {
      this.sync()
    }
  }

  disconnect() {
    this.audioTarget.removeEventListener("play", this.sync)
    this.audioTarget.removeEventListener("pause", this.sync)
    this.removeUnlockListeners()
  }

  toggle() {
    if (this.audioTarget.paused) {
      this.play()
    } else {
      this.pause()
    }
  }

  play() {
    this.audioTarget.play()
      .then(() => {
        this.storePreference(true)
        this.removeUnlockListeners()
        this.sync()
      })
      .catch(() => {
        if (this.shouldPlayByDefault()) {
          this.addUnlockListeners()
        } else {
          this.storePreference(false)
        }

        this.sync()
      })
  }

  pause() {
    this.audioTarget.pause()
    this.storePreference(false)
    this.removeUnlockListeners()
    this.sync()
  }

  sync() {
    const isPlaying = !this.audioTarget.paused

    this.buttonTarget.setAttribute("aria-pressed", isPlaying ? "true" : "false")
    this.buttonTarget.classList.toggle("is-active", isPlaying)
    this.labelTarget.textContent = isPlaying ? "Tune on" : "Tune off"
  }

  shouldPlayByDefault() {
    try {
      const preference = window.localStorage.getItem(STORAGE_KEY)

      if (preference === null) {
        return this.defaultOnValue
      }

      return preference === "1"
    } catch {
      return this.defaultOnValue
    }
  }

  storePreference(isPlaying) {
    try {
      window.localStorage.setItem(STORAGE_KEY, isPlaying ? "1" : "0")
    } catch {
      // Playback still works if storage is unavailable.
    }
  }

  addUnlockListeners() {
    if (this.unlockListenersAdded) return

    document.addEventListener("pointerdown", this.unlockPlayback, { once: true })
    document.addEventListener("keydown", this.unlockPlayback, { once: true })
    this.unlockListenersAdded = true
  }

  removeUnlockListeners() {
    if (!this.unlockListenersAdded) return

    document.removeEventListener("pointerdown", this.unlockPlayback)
    document.removeEventListener("keydown", this.unlockPlayback)
    this.unlockListenersAdded = false
  }

  unlockPlayback() {
    this.removeUnlockListeners()

    if (this.shouldPlayByDefault()) {
      this.play()
    }
  }
}
